import { useState, useEffect, useCallback, useMemo } from "react";
import { Select } from "antd";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
    Sliders, Plus, RefreshCw, Trash2, Pencil, X, Save, GripVertical, Lock, Eye, EyeOff,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import {
    ATTRIBUTES_URL, ATTRIBUTE_DETAIL, ATTRIBUTE_ASSIGNMENTS_URL,
    ATTRIBUTE_REORDER_URL, CUSTOMER_TYPE_MASTER_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

const CHOICE_TYPES = ["dropdown", "multiselect"];

const TYPE_LABEL = {
    text: "Text", textarea: "Paragraph", number: "Whole number",
    decimal: "Decimal", money: "Money", boolean: "Yes / No", date: "Date",
    dropdown: "Dropdown", multiselect: "Multi-select",
};

const emptyForm = {
    name: "", input_type: "text", unit: "", group: "", help_text: "",
    reference_source: "", is_required: false, is_filterable: false,
    optionsText: "", customer_type_ids: [], customer_category: "",
};

const CustomFields = () => {
    const { fetch, create, update, remove } = useApiMethods();

    const [rows,    setRows]    = useState([]);
    const [meta,    setMeta]    = useState({ input_types: [], reference_sources: [] });
    const [types,   setTypes]   = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving,  setSaving]  = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [form,    setForm]    = useState(emptyForm);
    const [editId,  setEditId]  = useState(null);

    // Drag state. `dragId` is the field being moved; `dropHint` is what the
    // pointer is currently over, so the row can show where it would land.
    const [dragId,   setDragId]   = useState(null);
    const [dropHint, setDropHint] = useState(null);   // { id } | { group }

    const load = useCallback(() => {
        setLoading(true);
        fetch(ATTRIBUTES_URL)
            .then(res => {
                if (res?.status === "success") {
                    setRows(res.data || []);
                    setMeta({
                        input_types: res.input_types || [],
                        reference_sources: res.reference_sources || [],
                    });
                } else {
                    ToastMsg("error", res?.message || "Failed to load custom fields");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load custom fields"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(CUSTOMER_TYPE_MASTER_URL)
            .then(res => { if (res?.status === "success") setTypes(res.data || []); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const grouped = useMemo(() => {
        const out = [];
        rows.forEach(r => {
            const label = r.group || "Other Details";
            let bucket = out.find(g => g.label === label);
            if (!bucket) {
                // rawGroup is what gets written back — "Other Details" is a
                // display label for the ungrouped bucket, not a real section.
                bucket = { label, rawGroup: r.group || "", items: [] };
                out.push(bucket);
            }
            bucket.items.push(r);
        });
        return out;
    }, [rows]);

    const isChoice = CHOICE_TYPES.includes(form.input_type);

    const submitNew = () => {
        if (!form.name.trim()) { ToastMsg("error", "Give the field a name"); return; }
        const options = form.optionsText.split("\n").map(s => s.trim()).filter(Boolean);
        if (isChoice && !form.reference_source && !options.length) {
            ToastMsg("error", "List the options, one per line — or pick a source list");
            return;
        }
        setSaving(true);
        create(ATTRIBUTES_URL, {
            name: form.name.trim(),
            input_type: form.input_type,
            unit: form.unit.trim(),
            group: form.group.trim(),
            help_text: form.help_text.trim(),
            reference_source: isChoice ? form.reference_source : "",
            is_required: form.is_required,
            is_filterable: form.is_filterable,
            options,
            assign_to_customer_types: form.customer_type_ids,
            assign_to_category: form.customer_category,
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", `"${form.name.trim()}" added`);
                    setForm(emptyForm);
                    setShowNew(false);
                    load();
                } else {
                    ToastMsg("error", res?.message || "Could not add the field");
                }
            })
            .catch(() => ToastMsg("error", "Could not add the field"))
            .finally(() => setSaving(false));
    };

    const saveEdit = (row) => {
        setSaving(true);
        const options = form.optionsText.split("\n").map(s => s.trim()).filter(Boolean);
        update(ATTRIBUTE_DETAIL(row.id), {
            name: form.name.trim() || row.name,
            unit: form.unit,
            group: form.group,
            help_text: form.help_text,
            is_required: form.is_required,
            is_filterable: form.is_filterable,
            ...(row.takes_options && !row.reference_source ? { options } : {}),
        })
            .then(res => {
                if (res?.status !== "success") {
                    ToastMsg("error", res?.message || "Could not update the field");
                    return null;
                }
                // Scoping lives on a separate endpoint
                return create(ATTRIBUTE_ASSIGNMENTS_URL, {
                    attribute_id: row.id,
                    customer_type_ids: form.customer_type_ids,
                    customer_category: form.customer_category,
                });
            })
            .then(res => {
                if (res && res.status === "success") {
                    ToastMsg("success", "Field updated");
                    setEditId(null);
                    load();
                } else if (res) {
                    ToastMsg("error", res?.message || "Could not update scoping");
                }
            })
            .catch(() => ToastMsg("error", "Could not update the field"))
            .finally(() => setSaving(false));
    };

    const startEdit = (row) => {
        setEditId(row.id);
        setForm({
            ...emptyForm,
            name: row.name,
            input_type: row.input_type,
            unit: row.unit || "",
            group: row.group || "",
            help_text: row.help_text || "",
            reference_source: row.reference_source || "",
            is_required: row.is_required,
            is_filterable: row.is_filterable,
            optionsText: (row.options || []).map(o => o.name).join("\n"),
            customer_type_ids: (row.assignments || [])
                .map(a => a.customer_type_id).filter(Boolean),
            customer_category: (row.assignments || [])[0]?.customer_category || "",
        });
    };

    /* Reorder by drag. The list is re-sequenced locally first so the row
       lands under the cursor immediately, then persisted in one request; a
       failure reloads from the server rather than leaving the screen showing
       an order the database does not have. */
    const persistOrder = (next) => {
        const payload = next.map((r, i) => ({
            id: r.id, sort_order: i, group: r.group || "",
        }));
        create(ATTRIBUTE_REORDER_URL, { order: payload })
            .then(res => {
                if (res?.status !== "success") {
                    ToastMsg("error", res?.message || "Could not save the new order");
                    load();
                }
            })
            .catch(() => {
                ToastMsg("error", "Could not save the new order");
                load();
            });
    };

    const handleDrop = (target) => {
        const sourceId = dragId;
        setDragId(null);
        setDropHint(null);
        if (!sourceId) return;

        const source = rows.find(r => r.id === sourceId);
        if (!source) return;

        // Dropping on a section heading moves the field to the end of that
        // section; dropping on a row inserts it at that row's position.
        const targetGroup = target.group !== undefined
            ? target.group
            : (rows.find(r => r.id === target.id)?.group || "");
        if (target.id === sourceId && (source.group || "") === targetGroup) return;

        const rest = rows.filter(r => r.id !== sourceId);
        const moved = { ...source, group: targetGroup };
        let at = rest.length;
        if (target.id !== undefined) {
            const i = rest.findIndex(r => r.id === target.id);
            if (i !== -1) at = i;
        } else {
            const last = rest.map(r => (r.group || "")).lastIndexOf(targetGroup);
            at = last === -1 ? rest.length : last + 1;
        }
        const next = [...rest.slice(0, at), moved, ...rest.slice(at)];
        setRows(next);
        persistOrder(next);
    };

    const toggleVisible = (row) => {
        const next = !row.is_active;
        if (!next && !window.confirm(
            `Hide "${row.name}"?

The sales team stops being asked for it. ` +
            `Leads that already have an answer keep showing it, read-only.`)) return;
        update(ATTRIBUTE_DETAIL(row.id), { is_active: next })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", next ? "Field shown" : "Field hidden");
                    load();
                } else ToastMsg("error", res?.message || "Could not change the field");
            })
            .catch(() => ToastMsg("error", "Could not change the field"));
    };

    const removeField = (row) => {
        if (!window.confirm(
            `Remove "${row.name}"?\n\nIt will stop appearing on lead pages. ` +
            `Any values already captured are kept in the database.`)) return;
        remove(ATTRIBUTE_DETAIL(row.id))
            .then(res => {
                if (res?.status === "success") { ToastMsg("success", "Field removed"); load(); }
                else ToastMsg("error", res?.message || "Could not remove the field");
            })
            .catch(() => ToastMsg("error", "Could not remove the field"));
    };

    const inputCls = "w-full text-xs px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-400";
    const labelCls = "text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1 block";

    const scopeText = (row) => {
        const rules = row.assignments || [];
        if (!rules.length) return "All customers";
        const names = rules.map(a => {
            const t = types.find(x => x.id === a.customer_type_id);
            return a.customer_type_id ? (t?.name || `Type ${a.customer_type_id}`) : "All types";
        });
        const cat = rules[0]?.customer_category;
        return names.join(", ") + (cat ? ` · ${cat.replace("_", " ")}` : "");
    };

    return (
        <ContainerCard>
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1 gap-3 flex-wrap">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Sliders className="h-4 w-4 text-indigo-500" />
                            Custom Fields
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Define the details you capture against a customer — they appear on every lead page automatically
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={load} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                        <Button onClick={() => { setShowNew(v => !v); setForm(emptyForm); setEditId(null); }}
                            size="sm" className="flex items-center gap-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
                            <Plus className="h-3 w-3" /> New Field
                        </Button>
                    </div>
                </div>

                {/* ── create form ── */}
                {showNew && (
                    <div className="mb-4 p-4 rounded-xl border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/15">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div>
                                <label className={labelCls}>Field name *</label>
                                <input className={inputCls} value={form.name} placeholder="e.g. Bed Count"
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                            </div>
                            <div>
                                <label className={labelCls}>Answer type *</label>
                                <Select value={form.input_type} size="small" className="w-full"
                                    onChange={v => setForm(f => ({ ...f, input_type: v, reference_source: "" }))}
                                    options={(meta.input_types.length
                                        ? meta.input_types
                                        : Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))
                                    ).map(t => ({ value: t.value, label: t.label }))} />
                            </div>
                            <div>
                                <label className={labelCls}>Section</label>
                                <input className={inputCls} value={form.group} placeholder="e.g. Hospital Capacity"
                                    onChange={e => setForm(f => ({ ...f, group: e.target.value }))} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                            <div>
                                <label className={labelCls}>Unit</label>
                                <input className={inputCls} value={form.unit} placeholder="beds, /day, users"
                                    onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelCls}>Helper text</label>
                                <input className={inputCls} value={form.help_text}
                                    placeholder="Shown to the rep as a hint"
                                    onChange={e => setForm(f => ({ ...f, help_text: e.target.value }))} />
                            </div>
                        </div>

                        {isChoice && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className={labelCls}>Where do the options come from?</label>
                                    <Select value={form.reference_source} size="small" className="w-full"
                                        onChange={v => setForm(f => ({ ...f, reference_source: v }))}
                                        options={(meta.reference_sources.length ? meta.reference_sources : [
                                            { value: "", label: "Use the options I type below" },
                                        ]).map(s => ({ value: s.value, label: s.label }))} />
                                </div>
                                {!form.reference_source && (
                                    <div>
                                        <label className={labelCls}>Options — one per line *</label>
                                        <textarea rows={3} className={cn(inputCls, "resize-none")}
                                            value={form.optionsText} placeholder={"Multi Speciality\nClinic\nDiagnostic Centre"}
                                            onChange={e => setForm(f => ({ ...f, optionsText: e.target.value }))} />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className={labelCls}>Applies to which customer types?</label>
                                <Select mode="multiple" allowClear value={form.customer_type_ids}
                                    size="small" className="w-full" placeholder="All customer types"
                                    onChange={v => setForm(f => ({ ...f, customer_type_ids: v }))}
                                    options={types.map(t => ({ value: t.id, label: t.name }))} />
                            </div>
                            <div>
                                <label className={labelCls}>Category</label>
                                <Select value={form.customer_category} size="small" className="w-full"
                                    onChange={v => setForm(f => ({ ...f, customer_category: v }))}
                                    options={[
                                        { value: "", label: "Prospects and channel partners" },
                                        { value: "prospect", label: "Prospects only" },
                                        { value: "channel_partner", label: "Channel partners only" },
                                    ]} />
                            </div>
                        </div>

                        <div className="flex items-center gap-4 mb-3">
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                <input type="checkbox" checked={form.is_required}
                                    onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))}
                                    className="rounded border-gray-300 dark:border-gray-600" />
                                Required
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                <input type="checkbox" checked={form.is_filterable}
                                    onChange={e => setForm(f => ({ ...f, is_filterable: e.target.checked }))}
                                    className="rounded border-gray-300 dark:border-gray-600" />
                                Offer as a filter
                            </label>
                        </div>

                        <div className="flex gap-2">
                            <Button onClick={submitNew} disabled={saving} size="sm"
                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8">
                                {saving ? "Adding…" : "Add Field"}
                            </Button>
                            <Button onClick={() => { setShowNew(false); setForm(emptyForm); }}
                                variant="outline" size="sm" className="text-xs h-8">Cancel</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── list ── */}
            {loading && !rows.length ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading custom fields…</p>
                    </div>
                </div>
            ) : !rows.length ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-sm px-6">
                        <Sliders className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No custom fields yet</p>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            Add one and it shows up on every matching lead straight away.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {grouped.map(group => (
                        <div key={group.label}>
                            <p onDragOver={e => { e.preventDefault(); setDropHint({ group: group.rawGroup }); }}
                                onDrop={e => { e.preventDefault(); handleDrop({ group: group.rawGroup }); }}
                                className={cn(
                                    "text-[10px] uppercase tracking-wide mb-1.5 px-1 py-0.5 rounded transition-colors",
                                    dropHint?.group === group.rawGroup && dragId
                                        ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300"
                                        : "text-gray-400 dark:text-gray-500")}>
                                {group.label}
                                {dropHint?.group === group.rawGroup && dragId && " — drop to move here"}
                            </p>
                            <div className="space-y-1.5">
                                {group.items.map(row => {
                                    const isEditing = editId === row.id;
                                    return (
                                        <div key={row.id}
                                            draggable={!isEditing}
                                            onDragStart={e => {
                                                setDragId(row.id);
                                                e.dataTransfer.effectAllowed = "move";
                                            }}
                                            onDragEnd={() => { setDragId(null); setDropHint(null); }}
                                            onDragOver={e => {
                                                if (!dragId || dragId === row.id) return;
                                                e.preventDefault();
                                                setDropHint({ id: row.id });
                                            }}
                                            onDrop={e => { e.preventDefault(); handleDrop({ id: row.id }); }}
                                            className={cn(
                                                "rounded-lg border bg-white dark:bg-gray-800 p-3 transition-all",
                                                dragId === row.id
                                                    ? "opacity-40 border-indigo-400"
                                                    : "border-gray-200 dark:border-gray-700",
                                                dropHint?.id === row.id && dragId && dragId !== row.id
                                                    && "border-indigo-500 ring-1 ring-indigo-400")}>
                                            {isEditing ? (
                                                <div className="space-y-3">
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                        <div>
                                                            <label className={labelCls}>Name</label>
                                                            <input className={inputCls} value={form.name}
                                                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Unit</label>
                                                            <input className={inputCls} value={form.unit}
                                                                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Section</label>
                                                            <input className={inputCls} value={form.group}
                                                                onChange={e => setForm(f => ({ ...f, group: e.target.value }))} />
                                                        </div>
                                                    </div>
                                                    {row.takes_options && !row.reference_source && (
                                                        <div>
                                                            <label className={labelCls}>Options — one per line</label>
                                                            <textarea rows={3} className={cn(inputCls, "resize-none")}
                                                                value={form.optionsText}
                                                                onChange={e => setForm(f => ({ ...f, optionsText: e.target.value }))} />
                                                            <p className="mt-1 text-[10px] text-gray-400">
                                                                Removing an option hides it from new entries; answers already
                                                                given keep their value.
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className={labelCls}>Customer types</label>
                                                            <Select mode="multiple" allowClear size="small" className="w-full"
                                                                placeholder="All customer types"
                                                                value={form.customer_type_ids}
                                                                onChange={v => setForm(f => ({ ...f, customer_type_ids: v }))}
                                                                options={types.map(t => ({ value: t.id, label: t.name }))} />
                                                        </div>
                                                        <div>
                                                            <label className={labelCls}>Category</label>
                                                            <Select size="small" className="w-full"
                                                                value={form.customer_category}
                                                                onChange={v => setForm(f => ({ ...f, customer_category: v }))}
                                                                options={[
                                                                    { value: "", label: "Prospects and channel partners" },
                                                                    { value: "prospect", label: "Prospects only" },
                                                                    { value: "channel_partner", label: "Channel partners only" },
                                                                ]} />
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                                            <input type="checkbox" checked={form.is_required}
                                                                onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))}
                                                                className="rounded border-gray-300 dark:border-gray-600" />
                                                            Required
                                                        </label>
                                                        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                                            <input type="checkbox" checked={form.is_filterable}
                                                                onChange={e => setForm(f => ({ ...f, is_filterable: e.target.checked }))}
                                                                className="rounded border-gray-300 dark:border-gray-600" />
                                                            Filterable
                                                        </label>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button onClick={() => saveEdit(row)} disabled={saving}
                                                            className="h-7 px-3 rounded-md text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                                                            <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save"}
                                                        </button>
                                                        <button onClick={() => setEditId(null)}
                                                            className="h-7 px-3 rounded-md text-[11px] border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-1">
                                                            <X className="h-3 w-3" /> Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-start gap-3">
                                                    <GripVertical
                                                        title="Drag to reorder, or onto another section to move it"
                                                        className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 mt-0.5 flex-shrink-0 cursor-grab active:cursor-grabbing hover:text-indigo-400" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                                                {row.name}
                                                            </span>
                                                            {row.is_required && (
                                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">required</span>
                                                            )}
                                                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                                {TYPE_LABEL[row.input_type] || row.input_type}
                                                            </span>
                                                            {row.unit && (
                                                                <span className="text-[9px] text-gray-400">in {row.unit}</span>
                                                            )}
                                                            {row.is_active === false && (
                                                                <span title="Not collected any more. Leads that already answered still show it."
                                                                    className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-200 flex items-center gap-0.5">
                                                                    <EyeOff className="h-2.5 w-2.5" /> hidden
                                                                </span>
                                                            )}
                                                            {row.in_use && (
                                                                <span title="Values have been captured, so the answer type is locked"
                                                                    className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex items-center gap-0.5">
                                                                    <Lock className="h-2.5 w-2.5" /> in use
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">
                                                            key <code className="text-gray-500 dark:text-gray-400">{row.slug}</code>
                                                            {" · "}{scopeText(row)}
                                                            {row.reference_source && " · options from master list"}
                                                            {row.takes_options && !row.reference_source &&
                                                                ` · ${(row.options || []).length} options`}
                                                        </p>
                                                        {row.help_text && (
                                                            <p className="mt-0.5 text-[10px] text-gray-400 italic">{row.help_text}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <button onClick={() => toggleVisible(row)}
                                                            title={row.is_active === false ? "Show this field again" : "Hide from the sales team"}
                                                            className="h-6 w-6 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                                                            {row.is_active === false
                                                                ? <EyeOff className="h-3 w-3" />
                                                                : <Eye className="h-3 w-3" />}
                                                        </button>
                                                        <button onClick={() => startEdit(row)} title="Edit"
                                                            className="h-6 w-6 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                                                            <Pencil className="h-3 w-3" />
                                                        </button>
                                                        <button onClick={() => removeField(row)} title="Remove"
                                                            className="h-6 w-6 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-rose-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </ContainerCard>
    );
};

export default CustomFields;
